/**
 * Quién puede hacer qué en una carpeta CERRADA.
 *
 * Reunión con las supervisoras (2026-09-18). Las carpetas se cierran el día
 * 5 de cada mes para que los clientes dejen de cargar, pero la contabilidad
 * se hace uno o dos meses después, con la carpeta ya cerrada. El problema era
 * que cualquier `level: 'all'` —contador y admin, casi cien personas— podía
 * además subir documentos, borrar, y abrir o cerrar carpetas cerradas: una
 * auxiliar borraba y volvía a cargar sin avisar.
 *
 * Regla acordada, con la carpeta cerrada:
 *  - SUPERVISORA y ROOT: todo (subir documentos, borrar, abrir y cerrar).
 *  - CONTADOR y ADMIN: solo el cajón del documento —contabilización,
 *    vinculados, referencias, marca de contabilizado, aprobación, XML—.
 *    Ni documentos nuevos en la carpeta ni borrar nada.
 *  - CLIENTE: nada, igual que antes.
 *
 * La supervisora se reconoce por `documents.supervisor === true` en su rol
 * GLOBAL (roles/supervisora). A propósito no se usa `folders.closing`: ese
 * flag también existe en las matrices por carpeta, y una matriz podría darle
 * los poderes de supervisora a una contadora en un cliente sin que se note.
 *
 * Antes cada permiso repetía `(!isClosing || level === 'all')`; ahora todos
 * consultan esta función. Módulo puro, para poder testearlo directo.
 */

/** Root o supervisora: los únicos que actúan sobre la carpeta cerrada. */
export const isSupervisor = (globalRole) => {
  const d = globalRole?.documents || {};
  return d.root === true || d.supervisor === true;
};

/**
 * Supervisora GLOBAL o root: la que ve la papelera de todos, los grupos y
 * todas las solicitudes. La supervisora de empresa (documents.scoped, ver
 * lib/user_scope.js) actúa sobre carpetas cerradas de sus empresas, pero no
 * es global.
 */
export const isGlobalSupervisor = (globalRole) =>
  isSupervisor(globalRole) && globalRole?.documents?.scoped !== true;

/**
 * @param {object}  p
 * @param {object}  p.globalRole  rol global del usuario (con .documents)
 * @param {boolean} p.isClosing   la carpeta está cerrada
 * @returns {{folderWrite:boolean, drawerWrite:boolean, drawerDelete:boolean}}
 *   folderWrite  → subir documentos, crear/editar/eliminar en la carpeta
 *   drawerWrite  → trabajar el cajón del documento (subir contabilización,
 *                  vinculados, XML, referencias, marcar, aprobar)
 *   drawerDelete → eliminar algo del cajón (siempre con token, además)
 *
 * Estos flags solo dicen si el CIERRE lo permite. Los permisos normales del
 * rol y de la matriz (delete, accounting, …) se siguen exigiendo aparte.
 */
export const closedFolderAccess = ({ globalRole, isClosing } = {}) => {
  if (!isClosing) return { folderWrite: true, drawerWrite: true, drawerDelete: true };
  const supervisor = isSupervisor(globalRole);
  const staff = globalRole?.documents?.level === 'all';
  return {
    folderWrite: supervisor,
    drawerWrite: supervisor || staff,
    drawerDelete: supervisor,
  };
};

/**
 * Cerrar carpeta (2026-09-24): lo hacen root, supervisora y contador. Se lee
 * `folders.closing` del rol GLOBAL, no del efectivo: el efectivo de level
 * 'all' siempre trae closing:true (permission_engine) y no distingue al admin,
 * que en producción también es level 'all' y no cierra.
 */
export const canCloseFolder = (globalRole) => globalCarpetas(globalRole).cerrar;

/**
 * Abrir una carpeta cerrada: root, supervisora, o quien root habilitó con
 * users/{uid}.Carpetas.abrir. Es un permiso aparte: no hace supervisora al
 * usuario.
 */
export const canOpenFolder = (globalRole) => globalCarpetas(globalRole).abrir;

/**
 * Abrir y cerrar carpetas (Juan, 2026-10-21). Tres capas:
 *  1. el ROL: abre root o supervisora; cierra además quien tiene
 *     folders.closing (contador);
 *  2. «Editar usuario»: users/{uid}.Carpetas = {abrir, cerrar} (llega como
 *     `globalRole.carpetas`, lib/user_scope.js) suma a lo del rol en TODAS sus
 *     empresas. Rol + usuario = lo GLOBAL;
 *  3. la matriz de empresa: permissions.carpetas = {abrir?, cerrar?} MANDA en
 *     esa empresa, para dar o para quitar. Solo guarda lo que difiere de lo
 *     global (companyCarpetasToSave): una clave ausente sigue a lo global.
 * A root nada se le quita. Solo root edita las capas 2 y 3 (firestore.rules).
 * El control es de pantalla: las reglas de folders/… no lo miran.
 */
export const CARPETAS_FLAGS = ['abrir', 'cerrar'];

/** Lo que da el ROL, sin la marca del usuario. */
export const roleGivesCarpetas = (role) => ({
  abrir: isSupervisor(role),
  cerrar: isSupervisor(role) || role?.folders?.closing === true,
});

/** Rol + users/{uid}.Carpetas: lo que vale si la empresa no decide. */
export const globalCarpetas = (globalRole) => {
  const byRole = roleGivesCarpetas(globalRole);
  const own = globalRole?.carpetas || {};
  return {
    abrir: byRole.abrir || own.abrir === true,
    cerrar: byRole.cerrar || own.cerrar === true,
  };
};

const decideIn = (flag, { globalRole, companyMatrix } = {}) => {
  if (globalRole?.documents?.root === true) return true;
  const company = companyMatrix?.carpetas?.[flag];
  return typeof company === 'boolean' ? company : globalCarpetas(globalRole)[flag];
};

/** ¿Cierra en esta empresa? companyMatrix: la normalizada (lib/company_access). */
export const canCloseFolderIn = (p) => decideIn('cerrar', p);

/** ¿Abre en esta empresa? */
export const canOpenFolderIn = (p) => decideIn('abrir', p);

/** Lo que se guarda en permissions.carpetas: solo lo que difiere de lo global. */
export const companyCarpetasToSave = ({ values, global } = {}) => {
  const out = {};
  CARPETAS_FLAGS.forEach((k) => {
    const v = values?.[k] === true;
    if (v !== (global?.[k] === true)) out[k] = v;
  });
  return out;
};
